import axios from 'axios';
import classNames from 'classnames';
import Button from 'components/base/Button';
import * as formik from 'formik';
import { errorHandler } from 'helpers/response-utils';
import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import { toast } from 'react-toastify';
import { currentUserProfileValidationSchema } from 'static/User';
import QRCode from 'qrcode';

const ClinicMyAccount = () => {
  const { Formik } = formik;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [qrCodeImage, setQrCodeImage] = useState('');
  const [clinic, setClinic] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    code: ''
  });
  const [user, setUser] = useState({
    firstname: '',
    email: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    getClinic();
    getCurrentUser();
  }, []);

  const getClinic = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/clinic/get`,
        { headers }
      );

      if (response.data.status === false) {
        errorHandler(response.data);
        return;
      }

      const data = response.data.data.clinic;
      setQrCodeImage(await QRCode.toDataURL(data.code));
      setClinic({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        postcode: data.postcode || '',
        address: data.address || '',
        code: data.code || ''
      });
    } catch (error) {
      console.error('Error occurred while loading clinic:', error);
    }
  };

  const getCurrentUser = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/user/me`,
        { headers }
      );

      if (response.data.status === false) {
        errorHandler(response.data);
        return;
      }

      const data = response.data.data.user;
      setUser({
        firstname: data.firstname || '',
        email: data.email || ''
      });
    } catch (error) {
      console.error('Error occurred while loading user profile:', error);
    }
  };

  const updateCurrentUser = async (values: typeof user) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_ENDPOINT}/user/me`,
        values,
        { headers }
      );

      if (response.data.status === false) {
        toast.error(response.data.error.message);
        return;
      }

      const updatedUser = response.data.data.user;
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem(
        'user',
        JSON.stringify({ ...storedUser, ...updatedUser })
      );
      setUser({
        firstname: updatedUser.firstname || '',
        email: updatedUser.email || ''
      });
      setIsEditing(false);
      toast.success(response.data.data.message);
    } catch (error) {
      console.error('Error occurred while updating user profile:', error);
    }
  };

  return (
    <Card className="col-12">
      <Card.Body>
        <Card className="mb-5">
          <Card.Header>
            <h3 className="mb-0">Clinic Information</h3>
          </Card.Header>
          <Card.Body>
            <Formik
              enableReinitialize={true}
              onSubmit={() => undefined}
              initialValues={clinic}
            >
              {({ handleSubmit, handleChange, values, touched, errors }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <Row className={classNames('g-3', 'mb-4')}>
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label>Clinic Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          disabled
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label>Clinic Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          disabled
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          type="text"
                          name="address"
                          value={values.address}
                          onChange={handleChange}
                          disabled
                        />
                      </Form.Group>
                    </Col>

                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          name="phone"
                          value={values.phone}
                          onChange={handleChange}
                          disabled
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={4}>
                      <Form.Group>
                        <Form.Label>Clinic Code</Form.Label>
                        <Form.Control value={values.code} disabled />
                      </Form.Group>
                    </Col>
                    <Col sm={4}>
                      <Form.Group>
                        <Form.Label>Postcode</Form.Label>
                        <Form.Control
                          type="text"
                          name="postcode"
                          value={values.postcode}
                          onChange={handleChange}
                          disabled
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={4}>
                      <Form.Group>
                        <Form.Label style={{ marginLeft: '120px' }}>
                          QR Code
                        </Form.Label>

                        <div className="d-flex justify-content-center align-items-center">
                          <a href={qrCodeImage} download={values.code}>
                            <img
                              src={qrCodeImage}
                              alt={`QR code for ${values.code}`}
                              style={{
                                width: '100px',
                                height: '100px',
                                display: 'block'
                              }}
                            />
                          </a>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">My Account</h3>
              {!isEditing && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            <Formik
              enableReinitialize={true}
              validationSchema={currentUserProfileValidationSchema}
              onSubmit={values => updateCurrentUser(values)}
              initialValues={user}
            >
              {({ handleSubmit, handleChange, values, touched, errors }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <Row className={classNames('g-3', 'mb-4')}>
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="firstname"
                          value={values.firstname}
                          onChange={handleChange}
                          disabled={!isEditing}
                          isValid={touched.firstname && !errors.firstname}
                          isInvalid={touched.firstname && !!errors.firstname}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.firstname}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          disabled={!isEditing}
                          isValid={touched.email && !errors.email}
                          isInvalid={touched.email && !!errors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    {isEditing && (
                      <Col className="text-end">
                        <Button
                          type="button"
                          variant="phoenix-primary"
                          className="me-2"
                          onClick={() => setIsEditing(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                          Update My Account
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );
};

export default ClinicMyAccount;
